import { Locale } from "@/Common/Models/Localization/Locale";
import { PageOptionSelector } from "@/ExportableFile/Models/PageOptionSelector";
import { FormNavigation } from "@/Patient/Components/MedicalNote/ObstetricalFile/Navigation/FormNavigation";
import { FormPage } from "@/Patient/Components/MedicalNote/ObstetricalFile/Navigation/FormPage";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { OntarioObstetricalFilePageIdEnum } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFilePageIdEnum";
import { IObstetricalFileProps, ObstetricalFile } from "../ObstetricalFile";
import { IObstetricalFilePageProps } from "../ObstetricalFilePage";
import { OntarioObstetricalFileOpr1 } from "../Ontario/Page/OPR1/OntarioObstetricalFileOpr1";
import { OntarioObstetricalFileOpr2 } from "../Ontario/Page/OPR2/OntarioObstetricalFileOpr2";
import { OntarioObstetricalFileOpr3 } from "../Ontario/Page/OPR3/OntarioObstetricalFileOpr3";
import { OntarioObstetricalFilePostnatal } from "../Ontario/Page/Postnatal/OntarioObstetricalFilePostnatal";
import { OntarioObstetricalFileResources } from "../Ontario/Page/Resources/OntarioObstetricalFileResources";

class OntarioObstetricalFile extends ObstetricalFile<OntarioObstetricalFileForm> {
    private readonly componentId = "ontario-obstetrical-file";
    protected obstetricalFileTypeCssClass = "ontario";

    protected getComponentId(): string {
        return this.componentId;
    }

    public componentWillMount(): void {
        super.componentWillMount();
        cacheStore.RequestApplicationStringAsync(Locale.en);
    }

    protected loadFormNavigation(props: IObstetricalFileProps): FormNavigation {
        const formsPages = [
            new FormPage({
                id: OntarioObstetricalFilePageIdEnum.Opr1.toString(),
                name: cacheStore.GetString("ObstetricalFile_OntarioPrenatalRecord1Label"),
                render: () => <OntarioObstetricalFileOpr1 {...this.buildFormProps()} />
            }),
            new FormPage({
                id: OntarioObstetricalFilePageIdEnum.Opr2.toString(),
                name: cacheStore.GetString("ObstetricalFile_OntarioPrenatalRecord2Label"),
                render: () => <OntarioObstetricalFileOpr2 {...this.buildFormProps()} />
            }),
            new FormPage({
                id: OntarioObstetricalFilePageIdEnum.Opr3.toString(),
                name: cacheStore.GetString("ObstetricalFile_OntarioPrenatalRecord3Label"),
                render: () => <OntarioObstetricalFileOpr3 {...this.buildFormProps()} />
            }),
            new FormPage({
                id: OntarioObstetricalFilePageIdEnum.Resources.toString(),
                name: cacheStore.GetString("ObstetricalFile_ResourcesLabel"),
                render: () => <OntarioObstetricalFileResources {...this.buildFormProps()} />
            })
        ];

        if (this.hasPostnatal(props)) {
            const postnatal = new FormPage({
                id: OntarioObstetricalFilePageIdEnum.PostNatal.toString(),
                name: cacheStore.GetString("ObstetricalFile_PostnatalLabel"),
                render: () => <OntarioObstetricalFilePostnatal {...this.buildFormProps()} />
            });

            formsPages.push(postnatal);
        }

        return new FormNavigation(formsPages, props.formDisplay.selectedPageId);
    }

    private buildFormProps(): IObstetricalFilePageProps<OntarioObstetricalFileForm> {
        return {
            ...this.props,
            form: this.state.form,
            contributionCreationDate: this.props.model.ContributionCreationDate,
            previousObstetricalFile: this.props.previousObstetricalFile,
            contributionType: this.props.model.ContributionType,
            readonly: this.props.isReadOnly,
            onFormUpdate: (form: OntarioObstetricalFileForm) => super.handleFormUpdate(form),
            onObstetricalCountersUpdate: (form: OntarioObstetricalFileForm) => super.handleObstetricalCountersUpdate(form),
            onObstetricalFileVitalSignLink: (form: OntarioObstetricalFileForm, emrTemplateId: string) => super.handleObstetricalFileVitalSignLink(form, emrTemplateId),
            onMedicalItemVitalSignUpdate: async (vitalSign: ObstetricalFileVitalSign, emrTemplateId: string) => await super.handleVitalSignUpdate(vitalSign, emrTemplateId),
            onPageUnmount: () => this.onPageUnmount(),
            fetchLabObservations: this.props.fetchLabObservations
        };
    }

    private hasPostnatal(props: IObstetricalFileProps): boolean {
        return (props.model.PostnatalDate && props.model.ContributionCreationDate >= props.model.PostnatalDate);
    }

    protected getSelectionPage(): PageOptionSelector[] {
        const pageSelections = [
            new PageOptionSelector({
                label: `${cacheStore.GetString("ObstetricalFile_OntarioPrenatalRecord1Label")}`,
                value: 1,
                checked: true
            }),
            new PageOptionSelector({
                label: `${cacheStore.GetString("ObstetricalFile_OntarioPrenatalRecord2Label")}`,
                value: 2,
                checked: true
            }),
            new PageOptionSelector({
                label: `${cacheStore.GetString("ObstetricalFile_OntarioPrenatalRecord3Label")}`,
                value: 3,
                checked: true
            }),
            new PageOptionSelector({
                label: `${cacheStore.GetString("ObstetricalFile_ResourcesLabel")}`,
                value: 4,
                checked: true
            })
        ];

        if (this.hasPostnatal(this.props)) {
            pageSelections.push(
                new PageOptionSelector({
                    label: `${cacheStore.GetString("ObstetricalFile_PostnatalLabel")}`,
                    value: 5,
                    checked: true
                })
            );
        }

        return pageSelections;
    }

    protected getPageNumbers(): number[] {
        if (this.hasPostnatal(this.props))
            return [1, 2, 3, 4, 5];

        return [1, 2, 3, 4];
    }
}

export { OntarioObstetricalFile };
